import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import FieldHeader from '../FieldContent/children/FieldHeader';
import FilterSection from '../FieldContent/children/FilterSection';
import FieldList from '../FieldContent/children/FieldList';
import FieldModals from '../FieldContent/children/FieldModals';
import FieldInfoPage from '../FieldContent/children/FieldInfoPage';
import { useFieldManagement } from '../FieldContent/hooks/useFieldManagement';
import { useModals } from '../FieldContent/hooks/useModals';
import { generateId } from '../FieldContent/hooks/useFieldManagement';
import { Field, Deposit, Section, Horizon, Bed } from '../FieldContent/types/types';
import { useLazyGetFieldUuidQuery, useLazyGetFieldWithChildrenQuery } from '../../../services/fieldService';

//@ts-ignore
import classes from './FieldContent.module.scss'
import { useDebounce } from '@consta/uikit/useDebounce/index';
import { useDuplicateDepositMutation } from '../../../services/depositService';
import { useDuplicateAreaMutation } from '../../../services/areaService';
import { useDuplicateLayerMutation } from '../../../services/layerService';
import { useDuplicateHorizonMutation } from '../../../services/horizonService';
import { useDuplicateModal } from './hooks/useDuplicateModal';
import DuplicateModal from './Modal/DuplicateModal';
import { useDeleteFieldMutation } from '../../../services/fieldService';
import { useDeleteDepositMutation } from '../../../services/depositService';
import { useDeleteAreaMutation } from '../../../services/areaService';
import { useDeleteHorizonMutation } from '../../../services/horizonService';
import { useDeleteLayerMutation } from '../../../services/layerService';
import { useDeleteModal } from '../../../hooks/useDeleteModal';
import DeleteModal from '../../Modals/DeleteModal/DeleteModal';
import { Pagination } from '@consta/uikit/Pagination';

const FieldContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fieldUuid } = useParams<{ fieldUuid: string }>();

    const [triggerGetFieldByUuid, { data: fieldData }] = useLazyGetFieldUuidQuery();
    const [getFieldsWithChildren, { data: fieldsData, isLoading: isLoadingFields }] = useLazyGetFieldWithChildrenQuery();

    const [duplicateDepositApi] = useDuplicateDepositMutation();
    const [duplicateAreaApi] = useDuplicateAreaMutation();
    const [duplicateLayerApi] = useDuplicateLayerMutation();
    const [duplicateHorizonApi] = useDuplicateHorizonMutation();

    const [deleteFieldApi] = useDeleteFieldMutation();
    const [deleteDepositApi] = useDeleteDepositMutation();
    const [deleteAreaApi] = useDeleteAreaMutation();
    const [deleteHorizonApi] = useDeleteHorizonMutation();
    const [deleteLayerApi] = useDeleteLayerMutation();

    const {
        deleteModal,
        openDeleteModal,
        closeDeleteModal,
        setDeleteError
    } = useDeleteModal();

    // Состояния для фильтрации и пагинации
    const [searchField, setSearchField] = useState<string>('');
    const [filter, setFilter] = useState<any>({});
    const [currentPage, setCurrentPage] = useState<number>(1);

    const itemsPerPage = 10;

    // Параметры для запроса полей с дочерними элементами
    const fieldsParams = {
        limit: itemsPerPage,
        offset: itemsPerPage * (currentPage - 1),
        search: searchField || undefined,
        ...Object.entries(filter ?? {}).reduce((acc: any, [key, value]: any) => {
            if (value && value.length > 0) {
                acc[key] = Array.isArray(value) ? value.join('|') : value;
            }
            return acc;
        }, {}),
    };

    // Единый запрос для получения всех данных
    useEffect(() => {
        if (!fieldUuid) {
            getFieldsWithChildren(fieldsParams);
        }
    }, [currentPage, searchField, filter, fieldUuid]);

    const {
        fields,
        setFields,
        selectedField,
        setSelectedField,
        depositsVisible,
        sectionsVisible,
        horizonsVisible,
        toggleDepositVisibility,
        toggleSectionVisibility,
        toggleHorizonVisibility,
        handleToggleVisibility,
        handleToggleDepositVisibility,
        handleToggleSectionVisibility,
        handleToggleHorizonVisibility,
    } = useFieldManagement();

    const {
        fieldModal,
        setFieldModal,
        depositModal,
        setDepositModal,
        sectionModal,
        setSectionModal,
        horizonModal,
        setHorizonModal,
        bedModal,
        setBedModal,
        localField,
        setLocalField,
        localDeposit,
        setLocalDeposit,
        localSection,
        setLocalSection,
        localHorizon,
        setLocalHorizon,
        localBed,
        setLocalBed,
        handleAddField,
        handleAddDeposit,
        handleAddSection,
        handleAddHorizon,
        handleAddBed,
        handleEditField,
        handleEditDeposit,
        handleEditSection,
        handleEditHorizon,
        handleEditBed
    } = useModals();

    const {
        duplicateModal,
        duplicateName,
        deepCopy,
        targetParentId,
        setDuplicateName,
        setDeepCopy,
        setTargetParentId,
        openDuplicateModal,
        closeDuplicateModal
    } = useDuplicateModal();

    // Загрузка данных месторождения при открытии страницы по UUID
    useEffect(() => {
        if (fieldUuid) {
            loadFieldByUuid(fieldUuid);
        }
    }, [fieldUuid]);

    // Обновление fields из API данных
    useEffect(() => {
        if (fieldsData?.results) {
            setFields(fieldsData.results);
        }
    }, [fieldsData]);

    const loadFieldByUuid = async (uuid: string) => {
        try {
            const result = await triggerGetFieldByUuid({ uuid }).unwrap();
            setSelectedField(result);
        } catch (error) {
            console.error('Ошибка загрузки месторождения:', error);
        }
    };

    const refreshData = () => {
        if (fieldUuid) {
            loadFieldByUuid(fieldUuid);
        } else {
            getFieldsWithChildren(fieldsParams);
        }
    };

    // Debounce для поиска
    const debounceSetSearchValue = useDebounce((value: string) => {
        setSearchField(value ?? '');
        setCurrentPage(1);
    }, 500);

    useEffect(() => {
        debounceSetSearchValue(searchField);
    }, [searchField]);

    // Обработчики для фильтров
    const handleCompanyChange = (company: any) => {
        setFilter((prev: any) => ({
            ...prev,
            companies: company?.value ? [company.value] : undefined
        }));
        setCurrentPage(1);
    };

    const handleTypeChange = (type: any) => {
        setFilter((prev: any) => ({
            ...prev,
            type: type?.value ? [type.value] : undefined
        }));
        setCurrentPage(1);
    };

    const handleStageChange = (stage: any) => {
        setFilter((prev: any) => ({
            ...prev,
            current_stage: stage?.value ? [stage.value] : undefined
        }));
        setCurrentPage(1);
    };

    const handleSearchChange = (searchText: string) => {
        setSearchField(searchText);
    };

    const handleSearch = () => {
        console.log('Выполнение поиска');
    };

    // Очистка всех фильтров
    const clearAllFilters = () => {
        setFilter({});
        setSearchField('');
        setCurrentPage(1);
    };

    const hasActiveFilters = Object.keys(filter || {}).length > 0 || searchField;

    // Функции для удаления
    const handleDeleteField = async (fieldUuid: string) => {
        try {
            await deleteFieldApi({ uuid: fieldUuid }).unwrap();
            refreshData();
            closeDeleteModal();
        } catch (error: any) {
            console.error('❌ Ошибка при удалении месторождения:', error);
            setDeleteError(error.data?.[0] || error.message);
        }
    };

    const handleDeleteDeposit = async (depositUuid: string) => {
        try {
            await deleteDepositApi({ uuid: depositUuid }).unwrap();
            refreshData();
            closeDeleteModal();
        } catch (error: any) {
            console.error('❌ Ошибка при удалении залежи:', error);
            setDeleteError(error.data?.[0] || error.message);
        }
    };

    const handleDeleteSection = async (areaUuid: string) => {
        try {
            await deleteAreaApi({ uuid: areaUuid }).unwrap();
            refreshData();
            closeDeleteModal();
        } catch (error: any) {
            console.error('❌ Ошибка при удалении участка:', error);
            setDeleteError(error.data?.[0] || error.message);
        }
    };

    const handleDeleteHorizon = async (horizonUuid: string) => {
        try {
            await deleteHorizonApi({ uuid: horizonUuid }).unwrap();
            refreshData();
            closeDeleteModal();
        } catch (error: any) {
            console.error('❌ Ошибка при удалении горизонта:', error);
            setDeleteError(error.data?.[0] || error.message);
        }
    };

    const handleDeleteBed = async (layerUuid: string) => {
        try {
            await deleteLayerApi({ uuid: layerUuid }).unwrap();
            refreshData();
            closeDeleteModal();
        } catch (error: any) {
            console.error('❌ Ошибка при удалении пласта:', error);
            setDeleteError(error.data?.[0] || error.message);
        }
    };

    // Функция подтверждения удаления
    const handleConfirmDelete = () => {
        if (!deleteModal.entityType || !deleteModal.entityData) return;

        const { entityType, entityData } = deleteModal;

        switch (entityType) {
            case 'field':
                handleDeleteField(entityData.uuid);
                break;
            case 'deposit':
                handleDeleteDeposit(entityData.uuid);
                break;
            case 'section':
                handleDeleteSection(entityData.uuid);
                break;
            case 'horizon':
                handleDeleteHorizon(entityData.uuid);
                break;
            case 'bed':
                handleDeleteBed(entityData.uuid);
                break;
        }
    };

    // Функции для дублирования
    const handleDuplicateDeposit = async (
        depositUuid: string,
        deposit: Deposit,
        name: string,
        deepCopy: boolean,
        targetFieldUuid: string
    ) => {
        try {
            await duplicateDepositApi({
                uuid: depositUuid,
                new_name: name,
                target_uuid: targetFieldUuid,
                deep_copy: deepCopy || false
            }).unwrap();

            refreshData();
            closeDuplicateModal();
        } catch (error) {
            console.error('❌ Ошибка при дублировании залежи:', error);
        }
    };

    const handleDuplicateSection = async (
        areaUuid: string,
        section: Section,
        name: string,
        deepCopy: boolean,
        targetDepositUuid: string
    ) => {
        try {
            await duplicateAreaApi({
                uuid: areaUuid,
                new_name: name,
                target_uuid: targetDepositUuid,
                deep_copy: deepCopy || false
            }).unwrap();

            refreshData();
            closeDuplicateModal();
        } catch (error) {
            console.error('❌ Ошибка при дублировании участка:', error);
        }
    };

    const handleDuplicateHorizon = async (
        horizonUuid: string,
        horizon: Horizon,
        name: string,
        deepCopy: boolean,
        targetAreaUuid: string
    ) => {
        try {
            await duplicateHorizonApi({
                uuid: horizonUuid,
                new_name: name,
                target_uuid: targetAreaUuid,
                deep_copy: deepCopy || false
            }).unwrap();

            refreshData();
            closeDuplicateModal();
        } catch (error) {
            console.error('❌ Ошибка при дублировании горизонта:', error);
        }
    };

    const handleDuplicateBed = async (
        layerUuid: string,
        bed: Bed,
        name: string,
        deepCopy: boolean,
        targetHorizonUuid: string
    ) => {
        try {
            await duplicateLayerApi({
                uuid: layerUuid,
                new_name: name,
                target_uuid: targetHorizonUuid,
                deep_copy: deepCopy || false
            }).unwrap();

            refreshData();
            closeDuplicateModal();
        } catch (error) {
            console.error('❌ Ошибка при дублировании пласта:', error);
        }
    };

    // Функции сохранения сущностей
    const handleSaveField = () => {
        if (!localField) return;
        refreshData();
        setFieldModal({ isOpen: false, field: null });
        setLocalField(null);
    };

    const handleSaveDeposit = () => {
        if (!localDeposit) return;
        refreshData();
        setDepositModal({ isOpen: false, fieldId: '', deposit: null });
        setLocalDeposit(null);
    };

    const handleSaveSection = () => {
        if (!localSection) return;
        refreshData();
        setSectionModal({ isOpen: false, fieldId: '', depositId: '', section: null });
        setLocalSection(null);
    };

    const handleSaveHorizon = () => {
        if (!localHorizon) return;
        refreshData();
        setHorizonModal({ isOpen: false, fieldId: '', depositId: '', sectionId: '', horizon: null });
        setLocalHorizon(null);
    };

    const handleSaveBed = () => {
        if (!localBed) return;
        refreshData();
        setBedModal({ isOpen: false, fieldId: '', depositId: '', sectionId: '', horizonId: '', bed: null });
        setLocalBed(null);
    };

    // Функция для перехода на страницу информации
    const handleInfoClick = (field: Field) => {
        setSelectedField(field);
        navigate(`/field/${field?.uuid}`);
    };

    const handleAreaAdded = () => {
        refreshData();
    };

    const handleConfirmDuplicate = (name: string, deepCopy: boolean, targetParentId: string) => {
        if (!duplicateModal.entityType || !duplicateModal.entityData || !duplicateModal.parentIds) return;

        const { entityType, entityData } = duplicateModal;

        switch (entityType) {
            case 'deposit':
                handleDuplicateDeposit(
                    entityData.uuid,
                    entityData,
                    name,
                    deepCopy || false,
                    targetParentId
                );
                break;
            case 'section':
                handleDuplicateSection(
                    entityData.uuid,
                    entityData,
                    name,
                    deepCopy || false,
                    targetParentId
                );
                break;
            case 'horizon':
                handleDuplicateHorizon(
                    entityData.uuid,
                    entityData,
                    name,
                    deepCopy || false,
                    targetParentId
                );
                break;
            case 'bed':
                handleDuplicateBed(
                    entityData.uuid,
                    entityData,
                    name,
                    deepCopy || false,
                    targetParentId
                );
                break;
        }
    };

    const handlePageChange = (newPage: number) => {
  setCurrentPage(newPage);
};

    // Рендер страницы информации о месторождении
    if (fieldUuid) {
        return (
            <FieldInfoPage
                selectedField={selectedField || fieldData}
                depositsVisible={depositsVisible}
                sectionsVisible={sectionsVisible}
                horizonsVisible={horizonsVisible}
                toggleDepositVisibility={toggleDepositVisibility}
                toggleSectionVisibility={toggleSectionVisibility}
                toggleHorizonVisibility={toggleHorizonVisibility}
            />
        );
    }

    const totalPages = fieldsData?.count ? Math.ceil(fieldsData.count / itemsPerPage) : 0;

    return (
    <div className={classes.container}>
        <FieldHeader onAddField={handleAddField} />

        <FilterSection
            onCompanyChange={handleCompanyChange}
            onTypeChange={handleTypeChange}
            onStageChange={handleStageChange}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            searchValue={searchField}
            onClearFilters={hasActiveFilters ? clearAllFilters : undefined}
        />

        <div className={classes.contentWrapper}>
            {isLoadingFields && <div className={classes.loading}>Загрузка данных...</div>}

            <div className={classes.listContainer}>
                <FieldList
                    fields={fields || []}
                    totalCount={fieldsData?.count || 0}
                    isLoading={isLoadingFields}
                    onToggleVisibility={handleToggleVisibility}
                    onInfoClick={handleInfoClick}
                    onEditField={handleEditField}
                    onDeleteField={(fieldUuid: string, field: any) => {
                        openDeleteModal('field', field);
                    }}
                    onAddDeposit={handleAddDeposit}
                    onEditDeposit={handleEditDeposit}
                    onDeleteDeposit={(fieldUuid: string, depositUuid: string, deposit: any) => {
                        openDeleteModal('deposit', deposit, { fieldUuid });
                    }}
                    onAddSection={handleAddSection}
                    onEditSection={handleEditSection}
                    onDeleteSection={(fieldUuid: string, depositUuid: string, areaUuid: string, section: any) => {
                        openDeleteModal('section', section, { fieldUuid, depositUuid });
                    }}
                    onAddHorizon={handleAddHorizon}
                    onEditHorizon={handleEditHorizon}
                    onDeleteHorizon={(fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string, horizon: any) => {
                        openDeleteModal('horizon', horizon, { fieldUuid, depositUuid, areaUuid });
                    }}
                    onAddBed={handleAddBed}
                    onEditBed={handleEditBed}
                    onDeleteBed={(fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string, layerUuid: string, bed: any) => {
                        openDeleteModal('bed', bed, { fieldUuid, depositUuid, areaUuid, horizonUuid });
                    }}
                    onToggleDepositVisibility={handleToggleDepositVisibility}
                    onToggleSectionVisibility={handleToggleSectionVisibility}
                    onToggleHorizonVisibility={handleToggleHorizonVisibility}
                    onDuplicateDeposit={(fieldUuid: string, depositUuid: string, deposit: any) => {
                        openDuplicateModal('deposit', deposit, { fieldUuid });
                    }}
                    onDuplicateSection={(fieldUuid: string, depositUuid: string, areaUuid: string, section: any) => {
                        openDuplicateModal('section', section, { fieldUuid, depositUuid });
                    }}
                    onDuplicateHorizon={(fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string, horizon: any) => {
                        openDuplicateModal('horizon', horizon, { fieldUuid, depositUuid, areaUuid });
                    }}
                    onDuplicateBed={(fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string, layerUuid: string, bed: any) => {
                        openDuplicateModal('bed', bed, { fieldUuid, depositUuid, areaUuid, horizonUuid });
                    }}
                    onAreaAdded={handleAreaAdded}
                />
            </div>

            {totalPages > 1 && (
                <div className={classes.pagination}>
                    <Pagination
                        items={totalPages}
                        value={currentPage}
                        onChange={handlePageChange}
                        showFirstPage
                        showLastPage
                        visibleCount={7}
                        size='s'
                    />
                </div>
            )}
        </div>

        <FieldModals
            fieldModal={fieldModal}
            setFieldModal={setFieldModal}
            depositModal={depositModal}
            setDepositModal={setDepositModal}
            sectionModal={sectionModal}
            setSectionModal={setSectionModal}
            horizonModal={horizonModal}
            setHorizonModal={setHorizonModal}
            bedModal={bedModal}
            setBedModal={setBedModal}
            localField={localField}
            setLocalField={setLocalField}
            localDeposit={localDeposit}
            setLocalDeposit={setLocalDeposit}
            localSection={localSection}
            setLocalSection={setLocalSection}
            localHorizon={localHorizon}
            setLocalHorizon={setLocalHorizon}
            localBed={localBed}
            setLocalBed={setLocalBed}
            onSaveField={handleSaveField}
            onSaveDeposit={handleSaveDeposit}
            onSaveSection={handleSaveSection}
            onSaveHorizon={handleSaveHorizon}
            onSaveBed={handleSaveBed}
        />
        
        <DuplicateModal
            isOpen={duplicateModal.isOpen}
            entityType={duplicateModal.entityType}
            entityData={duplicateModal.entityData}
            parentIds={duplicateModal.parentIds}
            duplicateName={duplicateName}
            deepCopy={deepCopy}
            targetParentId={targetParentId}
            onClose={closeDuplicateModal}
            onDuplicate={handleConfirmDuplicate}
            onNameChange={setDuplicateName}
            onDeepCopyChange={setDeepCopy}
            onTargetParentIdChange={setTargetParentId}
        />
        
        <DeleteModal
            isOpen={deleteModal.isOpen}
            entityType={deleteModal.entityType}
            entityData={deleteModal.entityData}
            error={deleteModal.error}
            onClose={closeDeleteModal}
            onDelete={handleConfirmDelete}
        />
    </div>
);
};

export default FieldContent;